import { ToggleButtonGroup, ToggleButton, Slider, Box, Typography, Grid, Paper, Tooltip, colors } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Chart from "react-google-charts";
import InfoIcon from '@mui/icons-material/Info';
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";


const END_PUBLISHING_YEAR = 2025;

interface PublisherSummaryResponse {
    results: PublisherSummary[]
    metadata: SummaryMetadata
}

interface DataMode {
    sortBy: string,
    graphTitle: string,
    apiUrl: string
}

interface PublisherSummary {    
    publisherName: string,
    articlesPublishedInTimeframeThatHaveBeenRetracted: number,
    numberOfRetractions: number,
    numberOfHighRiskArticles: number,
    totalAnalysed: number
}

interface SummaryMetadata {
    startYear: number,
    endYear: number
}

function valuetext(value: number) {
    return `${value}`;
}



function PublisherSummaryBarChart() {

    const [hasInitialData, setHasInitialData] = useState(false)
    const [publisherSummary, setPublisherSummary] = useState<any[][]>()
    const [yearRange, setYearRange] = useState<number[]>([2020, END_PUBLISHING_YEAR]);
    const [shownStartYear, setShownStartYear] = useState<number>(2020);
    const [shownEndYear, setShownEndYear] = useState<number>(END_PUBLISHING_YEAR);
    const startYearInUse = useRef(2020)
    const endYearInUse = useRef(END_PUBLISHING_YEAR)

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setYearRange(newValue as number[]);
    };

    const byHighRiskArticleMode: DataMode = {
        sortBy: 'total_high_risk',
        graphTitle: 'Publisher Summary (Order by High Risk Articles)',
        apiUrl: argosApiEntryPoint + '/stats/public/publisher-summary'
    }
    const byRetractionsMode: DataMode = {
        sortBy: 'total_retracted',
        graphTitle: 'Publisher Summary (Order by Retractions)',
        apiUrl: argosApiEntryPoint + '/stats/public/publisher-summary'
    }
    const [mode, setMode] = useState(byRetractionsMode)
    const columnHeaders: any[][] = [
        [
            { type: "string", label: "Publisher" },
            { type: "number", label: "High Risk Articles published in time period" },
            { type: "number", label: "Number of Retractions in time period" }
        ]
    ]


    const marks = [
        {
            value: 2014,
            label: '2014',
        },
        {
            value: 2020,
            label: '2020',
        },
        {
            value: END_PUBLISHING_YEAR,
            label: '' + END_PUBLISHING_YEAR,
        }
    ];

    useEffect(() => {

        async function fetchData() {
            startYearInUse.current = yearRange[0]
            endYearInUse.current = yearRange[1]
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(mode.apiUrl + "?sortBy=" + mode.sortBy + "&startYear=" + yearRange[0] + "&endYear=" + yearRange[1] + "&minSize=0"
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: PublisherSummaryResponse) => {
                    if (data.metadata.startYear === startYearInUse.current && data.metadata.endYear === endYearInUse.current) {
                        setHasInitialData(true)
                        setShownStartYear(data.metadata.startYear)
                        setShownEndYear(data.metadata.endYear)
                        let numericalSummaries: any[][] = data.results
                            .map(it => [
                                it.publisherName, 
                                it.numberOfHighRiskArticles,
                                it.numberOfRetractions])
                        setPublisherSummary(columnHeaders.concat(numericalSummaries))
                    }
                })
        }
        fetchData()
    }, [mode, yearRange]);

    var paddingHeight = 40;
    var rowHeight = (publisherSummary?.length == undefined ? 0 : publisherSummary?.length) * 50;
    var chartHeight = rowHeight + paddingHeight;

    const options = {
        legend: { textStyle: { color: '#003366', fontName: 'Roboto', fontSize: 14 } },
        hAxis: {
            title: "High Risk Articles and retractions",
            minValue: 0,
        },
        vAxis: {
            title: "Publisher",
        },
        height: chartHeight,
        chartArea: {
            height: rowHeight,
        },
        //colors: ['#D33F00', '#FF3B30', '#FF9500']
    };

    const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: string,
    ) => {
        if (newMode === "high-risk") {
            setMode(byHighRiskArticleMode)
        } else {
            setMode(byRetractionsMode)
        }
    };


    return <Paper elevation={5} sx={{ marginTop: 5, padding: 2, width: '90%' }}>
        <Grid container>
            <Grid item xs={12} padding={2}>
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                    {mode.graphTitle}
                    <Tooltip title="All articles published since 1st Jan 2014 have received a Scitility Risk Score.
Possible values are: low risk, medium risk, high risk. 
We calculate the Scitility Risk Score by looking at the cascade effect of retractions on authors and citations. We take into account the reasons for the retraction derived from the retraction. Examples of High Risk retraction reasons include but are not limited to: 'Ethical Violations by Author', 'Falsification/Fabrication of Results', 'Manipulation of Images' and 'Fake Peer Review'.
The Scitility Risk Score is recalculated daily. We include all the facts that are available at our sources and part of public record.
Having a high Scitility Risk Score indicates that the authorship and/or citations are impacted by cascade effect of retractions and could justify further analysis. It is NOT an indicator of the quality of the particular article." placement="top-start">
                        <InfoIcon color="primary" fontSize="small" style={{ verticalAlign: 'top' }} />
                    </Tooltip>
                </Typography>
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h6">
                    {"Selected timeframe: 1st Jan " + shownStartYear + " - 31st Dec " + shownEndYear}
                </Typography>
            </Grid>
            <Grid item xs={12} md={6} padding={2}>
                <ToggleButtonGroup
                    color="primary"
                    value={mode.sortBy}
                    exclusive
                    onChange={handleChange}
                    aria-label="Graph Mode"
                >
                    <ToggleButton value="high-risk">By High Risk Articles</ToggleButton>
                    <ToggleButton value="retractions">By Retractions</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={6} sx={{ paddingRight: 2, paddingTop: 3 }}>
                <Slider
                    aria-labelledby="track-inverted-range-slider"
                    getAriaValueText={valuetext}
                    defaultValue={[2020, END_PUBLISHING_YEAR]}
                    onChange={handleSliderChange}
                    min={2014}
                    valueLabelDisplay="auto"
                    max={END_PUBLISHING_YEAR}
                    marks={marks}
                />
            </Grid>

            {hasInitialData &&
                <Grid item xs={12}>
                    <Chart
                        chartType="BarChart"
                        width="100%"
                        data={publisherSummary}
                        options={options}
                    />
                </Grid>
            }
            <Grid item xs={12} paddingRight={2} >
                <Typography fontFamily='Roboto' color='#003366' align="right" sx={{ fontStyle: 'italic' }}>
                    Powered by Scitility PBC
                </Typography>
            </Grid>
        </Grid>
    </Paper>
}

export default PublisherSummaryBarChart
